import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';

// Galleries aligned by index with translations.house.items
// Each entry: { thumb: URL, gallery: [URL, URL, ...] }
// Untouched items (Piscine, Bain nordique) keep their original single image
const galleries = [
  // 0. RedHouse — 8 photos (retirées : 144 doublon 142, 161 doublon 159 + déplacée en Hero)
  {
    thumb: '/photos/redhouse/141.jpg',
    gallery: [
      '/photos/redhouse/141.jpg',
      '/photos/redhouse/142.jpg',
      '/photos/redhouse/145.jpg',
      '/photos/redhouse/146.jpg',
      '/photos/redhouse/150.jpg',
      '/photos/redhouse/151.jpg',
      '/photos/redhouse/157.jpg',
      '/photos/redhouse/159.jpg',
    ],
  },
  // 1. Salons — 8 photos. Vignette = 023 (temp → 004 prévu mais 004 réaffecté à cuisine, on garde 023).
  {
    thumb: '/photos/salons/023.jpg',
    gallery: [
      '/photos/salons/012.jpg',
      '/photos/salons/016.jpg',
      '/photos/salons/019.jpg',
      '/photos/salons/021.jpg',
      '/photos/salons/022.jpg',
      '/photos/salons/023.jpg',
      '/photos/salons/026.jpg',
      '/photos/salons/037.jpg',
    ],
  },
  // 2. Cuisine & salle à manger — 4 photos. Vignette = 045.
  {
    thumb: '/photos/cuisine/045.jpg',
    gallery: [
      '/photos/cuisine/004.jpg',
      '/photos/cuisine/017.jpg',
      '/photos/cuisine/045.jpg',
      '/photos/cuisine/048.jpg',
    ],
  },
  // 3. Chambres — 16 photos (retirées : 106, 109 doublons 107 ; 113 doublon 114 ; 118 doublon 120). Vignette = 114.
  {
    thumb: '/photos/chambres/114.jpg',
    gallery: [
      '/photos/chambres/054.jpg',
      '/photos/chambres/055.jpg',
      '/photos/chambres/064.jpg',
      '/photos/chambres/080.jpg',
      '/photos/chambres/083.jpg',
      '/photos/chambres/089.jpg',
      '/photos/chambres/092.jpg',
      '/photos/chambres/101.jpg',
      '/photos/chambres/107.jpg',
      '/photos/chambres/114.jpg',
      '/photos/chambres/115.jpg',
      '/photos/chambres/117.jpg',
      '/photos/chambres/120.jpg',
      '/photos/chambres/127.jpg',
      '/photos/chambres/129.jpg',
      '/photos/chambres/138.jpg',
    ],
  },
  // 4. Bain nordique — new photo (2026-05)
  {
    thumb: '/photos/piscine/bainnordique.webp',
    gallery: ['/photos/piscine/bainnordique.webp'],
  },
  // 5. Piscine — new photo (2026-05 terrasse bois)
  {
    thumb: '/photos/piscine/pool.jpg',
    gallery: ['/photos/piscine/pool.jpg'],
  },
];

const AccommodationSection = () => {
  const { t, translations, lang } = useLang();
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const items = translations.house.items;

  const currentGallery = lightboxIdx !== null ? galleries[lightboxIdx].gallery : [];
  const hasMultiple = currentGallery.length > 1;

  const openLightbox = (idx) => {
    setLightboxIdx(idx);
    setPhotoIdx(0);
  };

  const closeLightbox = () => setLightboxIdx(null);
  const next = () => setPhotoIdx((i) => (i + 1) % currentGallery.length);
  const prev = () => setPhotoIdx((i) => (i - 1 + currentGallery.length) % currentGallery.length);

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight' && hasMultiple) next();
      else if (e.key === 'ArrowLeft' && hasMultiple) prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, hasMultiple]);

  return (
    <section id="house" data-testid="accommodation-section" className="py-24 md:py-32 bg-[#3D2020]">
      <div className="px-5 md:px-10 lg:px-14 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16"
        >
          <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-4 font-medium">
            {t('house.label')}
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-6">
            {t('house.title')}
          </h2>
          <p className="text-base md:text-lg text-white/70 leading-relaxed">
            {t('house.desc')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group cursor-pointer"
              onClick={() => openLightbox(i)}
              data-testid={`accommodation-card-${i}`}
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden mb-4 bg-white/5">
                <img
                  src={galleries[i].thumb}
                  alt={item.title[lang]}
                  loading="lazy"
                  className="img-cover"
                />
              </div>
              <h3 className="font-heading font-bold text-base text-white mb-1">
                {item.title[lang]}
              </h3>
              <p className="text-sm text-white/60">
                {item.desc[lang]}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {lightboxIdx !== null && (
        <div
          className="lightbox-overlay"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          <button
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-20"
            onClick={closeLightbox}
            aria-label="Fermer"
            data-testid="lightbox-close"
          >
            <X size={28} />
          </button>

          <img
            src={currentGallery[photoIdx]}
            alt={items[lightboxIdx].title[lang]}
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {hasMultiple && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-colors z-20"
                aria-label="Précédent"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-colors z-20"
                aria-label="Suivant"
                data-testid="lightbox-next"
              >
                <ChevronRight size={32} />
              </button>
              <div
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1.5 rounded-full"
                data-testid="lightbox-counter"
              >
                {photoIdx + 1} / {currentGallery.length}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default AccommodationSection;
